'use client'

import Link from 'next/link'
import { X, MessageSquare, LogOut, ChevronRight, Compass } from 'lucide-react'
import { Button } from './Button'
import { Card, CardHeader, CardContent } from './Card'
import { useAccountModalStore } from '../stores/accountModalStore'
import { useFeedbackStore } from '../stores/feedbackStore'
import { useAuth } from '../hooks/useAuth'
import { usePwaLaunchSource } from '../hooks/usePwaLaunchSource'
import { useUnreadFeedback } from '../hooks/useUnreadFeedback'
import { ShareAppCard } from './ShareAppCard'
import { describeBuild } from '../lib/buildVersion'

/**
 * Global account / settings surface, opened from any header's account avatar.
 * Rendering it as a modal (rather than navigating to /settings) means it works
 * identically in every context — including installed per-app PWAs whose
 * manifest scope would otherwise bounce a /settings link out to the browser.
 */
export function AccountModal() {
  const { isOpen, close } = useAccountModalStore()
  const { openFeedback } = useFeedbackStore()
  const { user, signOut } = useAuth()
  const launchSource = usePwaLaunchSource()
  const unread = useUnreadFeedback()

  if (!isOpen) return null

  // In a locked per-app PWA there's no dashboard or sidebar to populate, so
  // app discovery is irrelevant — hide it there.
  const showExplore = launchSource !== 'app'

  const initial = user?.email?.[0]?.toUpperCase() || 'U'

  // Both env vars are inlined at build time (next.config.ts), so this reflects
  // the bundle actually running — exactly what "how updated am I?" asks.
  const { versionLabel, builtAt } = describeBuild({
    buildId: process.env.NEXT_PUBLIC_BUILD_ID,
    buildTime: process.env.NEXT_PUBLIC_BUILD_TIME,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={close} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Account &amp; settings</h2>
          <button
            onClick={close}
            aria-label="Close"
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto">
          {/* Support — surfaced first: create a new request, or track existing ones */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">Support</h2>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={() => {
                  close()
                  openFeedback()
                }}
                className="w-full justify-center"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Send feedback
              </Button>
              {/* Shown everywhere — installed per-app PWA users need to reach
                  their tickets too. /support is outside a per-app manifest scope,
                  so tapping it there opens in the browser; that's an acceptable
                  trade for being able to see your requests at all. */}
              <Link
                href="/support"
                onClick={close}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 hover:border-gray-300 hover:bg-gray-50 transition-colors"
              >
                <div>
                  <p className="font-medium text-gray-900">
                    My support requests
                    {unread > 0 && (
                      <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white align-middle">
                        {unread > 9 ? '9+' : unread}
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {unread > 0
                      ? `Support replied to ${unread === 1 ? 'a request' : `${unread} requests`} — take a look.`
                      : "See the status of feedback you've sent us."}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
              </Link>
            </CardContent>
          </Card>

          {/* Identity */}
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold text-gray-900">Account</h2>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <span className="text-base font-medium text-blue-700">{initial}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.email || 'Signed in'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Share the app you're currently in — scopes a friend's signup to it.
              Renders nothing outside an /apps/<id> route. */}
          <ShareAppCard />

          {/* App discovery — only where there's a dashboard to populate.
              Adding/removing apps lives on the explore page, not in here. */}
          {showExplore && (
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold text-gray-900">Apps</h2>
              </CardHeader>
              <CardContent>
                <Link
                  href="/explore"
                  onClick={close}
                  className="flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2.5 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Compass className="w-5 h-5 text-gray-400 shrink-0" />
                    <div>
                      <p className="font-medium text-gray-900">Explore apps</p>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Preview every app and choose what&apos;s on your dashboard.
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Account actions */}
          <div className="flex">
            <Button
              variant="ghost"
              onClick={() => {
                close()
                signOut()
              }}
              className="justify-start text-red-600 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>

          {/* Build version — which deploy this bundle came from */}
          <p className="text-xs text-gray-400 text-center">
            {versionLabel}
            {builtAt && (
              <>
                {' · '}
                {builtAt.toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}
